import React, { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import { TopicEntry } from '@/components/cms/types/entry';

import Badge from '../../atoms/badge';

import CrossIcon from '~/svg/IconCross.svg';
import { useTranslation } from '@/lib/i18n/i18n.client';

type Props = {
  isFiltering: boolean;
  featuredTopics: TopicEntry[];
  allTopics: TopicEntry[];
  selectedTopic?: TopicEntry['topic'];
  handleFilterClick: (topic: string) => void;
};

export const Filters = ({
  isFiltering,
  featuredTopics,
  allTopics,
  selectedTopic,
  handleFilterClick,
}: Props) => {
  const { texts } = useTranslation();

  const nonFeaturedTopics = useMemo(() => {
    if (featuredTopics.length > 0) {
      return allTopics?.filter((topic) => {
        const hasMatch = featuredTopics.some(
          (featuredTopic) => topic.topic === featuredTopic.topic
        );
        return !hasMatch;
      });
    }
    return allTopics;
  }, [allTopics, featuredTopics]);

  const [isTopicsExpanded, setIsTopicsExpanded] = useState(() => {
    if (featuredTopics.length === 0) return true;
    if (selectedTopic) {
      const isSelectedTopicHidden = nonFeaturedTopics.some(
        (nonFeaturedTopic) => nonFeaturedTopic.slug === selectedTopic
      );
      return isSelectedTopicHidden;
    }
    return false;
  });

  return (
    <ul
      className={cn(
        'flex flex-1 flex-wrap gap-2',
        isFiltering && 'animate-pulse'
      )}
    >
      <FilterItem
        topic={{ slug: '', topic: texts.articles_feed.all_topics }}
        isSelected={!selectedTopic}
        handleClick={handleFilterClick}
      />
      {featuredTopics?.map((topic) => (
        <FilterItem
          key={topic.slug}
          topic={topic}
          isSelected={selectedTopic === topic.slug}
          handleClick={handleFilterClick}
        />
      ))}
      {isTopicsExpanded &&
        nonFeaturedTopics?.map((topic) => (
          <FilterItem
            key={topic.slug}
            topic={topic}
            isSelected={selectedTopic === topic.slug}
            handleClick={handleFilterClick}
          />
        ))}
      {featuredTopics.length > 0 && (
        <li>
          <Badge
            variant='outlined'
            colors='brand-500'
            classNames={{ root: 'cursor-pointer' }}
            onClick={() => setIsTopicsExpanded(!isTopicsExpanded)}
          >
            {isTopicsExpanded ? (
              <div className='h-3 w-3 rotate-180 duration-300'>
                <CrossIcon />
              </div>
            ) : (
              '...'
            )}
          </Badge>
        </li>
      )}
    </ul>
  );
};

const FilterItem = ({
  topic,
  handleClick,
  isSelected,
}: {
  topic: { slug: TopicEntry['slug']; topic: TopicEntry['topic'] };
  handleClick: (topic: string) => void;
  isSelected: boolean;
}) => {
  return (
    <li>
      <Badge
        variant={isSelected ? 'filled' : 'outlined'}
        classNames={{
          root: isSelected ? 'cursor-default' : 'cursor-pointer',
        }}
        colors='brand-100'
        onClick={() => handleClick(topic.slug)}
      >
        {topic.topic.toUpperCase()}
      </Badge>
    </li>
  );
};
